import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="videojs-player"
// Robust Video.js + VHS (HLS) controller that:
// - Avoids auto-init races by letting Stimulus own initialization
// - Forces VHS XHR to send CloudFront signed cookies (withCredentials)
// - Supports both server-provided src (data-videojs-player-src-value) and loadVideo()
export default class extends Controller {
  static values = {
    playerId: { type: String, default: "my_video_1" },
    src: String
  }

  connect() {
    this.player = null

    console.log("[videojs-player] Controller connected", {
      playerId: this.playerIdValue,
      controllerElementId: this.element.id,
      hasSrcValue: this.hasSrcValue
    })

    // Child element that Video.js binds to
    this.videoElement =
      this.element.querySelector("video-js") || this.element.querySelector("video")

    if (!this.videoElement) {
      console.warn("[videojs-player] No video-js or video element found")
      return
    }

    this.waitForVideoJS(() => {
      this.initializePlayer()

      // If the server provided an initial src, set it immediately
      if (this.hasSrcValue && this.srcValue) {
        console.log("[videojs-player] Setting initial src from data value:", this.srcValue)
        this.setSource(this.srcValue)
      } else {
        console.log("[videojs-player] No initial src provided; waiting for loadVideo()")
      }
    })
  }

  disconnect() {
    if (this.player && typeof this.player.dispose === "function") {
      try {
        this.player.dispose()
      } catch (e) {
        console.warn("[videojs-player] Error disposing player:", e)
      }
    }
    this.player = null
  }

  /**
   * Public method to load a video - can be called directly from JavaScript
   * Accepts:
   * - a URL string, or
   * - { preptestNumber, filename } for explanation videos, or
   * - { videoName } for lesson videos
   */
  loadVideo(source) {
    console.log("[videojs-player] loadVideo called", {
      source,
      hasPlayer: !!this.player
    })

    const videoUrl = this.buildVideoUrl(source)
    if (!videoUrl) return

    // Ensure player exists
    if (!this.player) {
      console.log("[videojs-player] Player not initialized yet; initializing then setting src")
      this.waitForVideoJS(() => {
        this.initializePlayer()
        this.setSource(videoUrl)
      })
      return
    }

    this.setSource(videoUrl)
  }

  buildVideoUrl(source) {
    let videoUrl

    if (typeof source === "string") {
      videoUrl = source
    } else if (source && source.preptestNumber && source.filename) {
      // Explanation video
      videoUrl = `https://cdn.lsatengine.com/explanations/${source.preptestNumber}/${source.filename}/${source.filename}.m3u8`
    } else if (source && source.videoName) {
      // Lesson video
      videoUrl = `https://cdn.lsatengine.com/lessons/${source.videoName}/${source.videoName}.m3u8`
    } else {
      console.error("[videojs-player] Invalid source provided", source)
      return null
    }

    console.log("[videojs-player] Video URL:", videoUrl)
    return videoUrl
  }

  waitForVideoJS(callback, retryCount = 0) {
    const maxRetries = 50 // 5 seconds max wait (50 * 100ms)

    if (window.videojs) {
      console.log("[videojs-player] video.js is available")
      // Give the browser a moment to finish parsing + attaching the <video-js> element
      setTimeout(callback, 50)
    } else if (retryCount < maxRetries) {
      console.log(
        `[videojs-player] Waiting for video.js to load... (attempt ${retryCount + 1}/${maxRetries})`
      )
      setTimeout(() => this.waitForVideoJS(callback, retryCount + 1), 100)
    } else {
      console.error("[videojs-player] video.js failed to load after maximum retries")
    }
  }

  initializePlayer() {
    // Prevent double-init
    if (this.player) return

    if (!this.videoElement) {
      this.videoElement =
        this.element.querySelector("video-js") || this.element.querySelector("video")
    }
    if (!this.videoElement) {
      console.error("[videojs-player] No video element found for initialization")
      return
    }

    const playerId = this.playerIdValue || this.videoElement.id

    console.log("[videojs-player] Initializing player", {
      playerId,
      videoElementId: this.videoElement.id
    })

    try {
      this.player = window.videojs(playerId, {
        playbackRates: [0.6, 0.8, 1, 1.2, 1.4, 1.6],
        html5: {
          vhs: {
            withCredentials: true
          }
        }
      })

      this.player.volume(0.5)

      // Belt-and-suspenders: enforce credentials once tech/VHS is ready
      this.player.ready(() => {
        const tech = this.player.tech && this.player.tech(true)
        if (tech && tech.vhs) {
          tech.vhs.withCredentials = true
          console.log("[videojs-player] VHS withCredentials enforced on ready()")
        }
      })

      // Helpful error logging
      this.player.on("error", () => {
        console.warn("[videojs-player] Video.js error:", this.player.error())
      })

      console.log("[videojs-player] Player initialized", { playerId })
    } catch (error) {
      console.error("[videojs-player] Error initializing player:", error)
      this.player = null
    }
  }

  setSource(videoUrl) {
    if (!this.player) {
      console.warn("[videojs-player] setSource called before player initialization")
      return
    }

    console.log("[videojs-player] Setting source:", videoUrl)

    try {
      this.player.src({
        src: videoUrl,
        type: "application/x-mpegURL"
      })
      this.player.volume(0.5)
    } catch (error) {
      console.error("[videojs-player] Error setting source:", error)
    }
  }
}
